import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, Calendar, Search, XCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { communicationService } from '../../../services/communicationService';
import { supabase } from '../../../lib/supabase';

const CampaignModal = ({ isOpen, onClose, campaign = null, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    messageType: '', // 'SMS', 'WHATSAPP', or 'BOTH'
    templateIds: [],
    campaignType: 'DYNAMIC',
    description: '',
    recipientCriteria: {
      status: [],
      city: null,
      ageCategory: [],
      recipientType: ['POC'] // Default to POC (backward compatible)
    },
    scheduledAt: null,
    runtimeMessage: '' // Runtime message for templates with is_runtime_message=true
  });
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [cities, setCities] = useState([]);
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [templatePage, setTemplatePage] = useState(1);
  const [templateTotal, setTemplateTotal] = useState(0);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [selectedTemplatesData, setSelectedTemplatesData] = useState([]); // Store full template data for selected ones

  useEffect(() => {
    if (isOpen) {
      loadCities();
      if (campaign) {
        const templateIds = campaign.targeted_provider_message_template_ids || [];
        setFormData({
          name: campaign.name || '',
          messageType: '', // Will be detected from templates after they load
          templateIds: templateIds,
          campaignType: campaign.communication_campaign_type || 'DYNAMIC',
          description: campaign.communication_campaign_desc || '',
          recipientCriteria: {
            ...(campaign.recipient_criteria || {}),
            recipientType: campaign.recipient_criteria?.recipientType || ['POC']
          },
          scheduledAt: campaign.scheduled_at ? new Date(campaign.scheduled_at).toISOString().slice(0, 16) : null,
          runtimeMessage: campaign.runtime_message || ''
        });
        
        // Load selected templates first to detect messageType
        if (templateIds.length > 0) {
          Promise.all(templateIds.map(id => communicationService.getTemplateById(id)))
            .then(fetched => {
              setSelectedTemplatesData(fetched);
              // Detect messageType from fetched templates
              const smsTemplates = fetched.filter(t => t.message_type === 'SMS');
              const whatsappTemplates = fetched.filter(t => t.message_type === 'WHATSAPP');
              let detectedType = '';
              if (smsTemplates.length > 0 && whatsappTemplates.length > 0) {
                detectedType = 'BOTH';
              } else if (smsTemplates.length > 0) {
                detectedType = 'SMS';
              } else if (whatsappTemplates.length > 0) {
                detectedType = 'WHATSAPP';
              }
              if (detectedType) {
                setFormData(prev => ({ ...prev, messageType: detectedType }));
              }
            })
            .catch(err => console.error('Error loading selected templates:', err));
        }
      } else {
        setFormData({
          name: '',
          messageType: '',
          templateIds: [],
          campaignType: 'DYNAMIC',
          description: '',
          recipientCriteria: {
            status: [],
            city: null,
            ageCategory: [],
            recipientType: ['POC']
          },
          scheduledAt: null,
          runtimeMessage: ''
        });
        setSelectedTemplatesData([]);
      }
      setErrors({});
      setTemplateSearchQuery(''); // Clear search when modal opens/closes
      setTemplatePage(1); // Reset pagination
    }
  }, [isOpen, campaign]);


  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(templateSearchQuery);
      setTemplatePage(1); // Reset to first page when search changes
    }, 300);

    return () => clearTimeout(timer);
  }, [templateSearchQuery]);

  const loadTemplates = async (page = 1, append = false) => {
    if (!formData.messageType) return; // Don't load if no message type selected
    
    setTemplateLoading(true);
    try {
      const filters = {
        isActive: true,
        page: page,
        pageSize: 50, // Load 50 at a time for better performance
        triggerType: 'MANUAL' // Only show templates that can be used manually (MANUAL or BOTH)
      };

      // Add message type filter
      if (formData.messageType === 'SMS') {
        filters.messageType = 'SMS';
      } else if (formData.messageType === 'WHATSAPP') {
        filters.messageType = 'WHATSAPP';
      }
      // For BOTH, we don't filter by messageType, we'll filter client-side

      // Add search filter
      if (debouncedSearchQuery.trim().length > 0) {
        filters.search = debouncedSearchQuery;
      }

      const result = await communicationService.getAllTemplates(filters);
      
      if (append) {
        setTemplates(prev => [...prev, ...(result.data || [])]);
      } else {
        setTemplates(result.data || []);
      }
      setTemplateTotal(result.total || 0);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setTemplateLoading(false);
    }
  };

  // Load templates when messageType, debouncedSearchQuery, or page changes
  useEffect(() => {
    if (formData.messageType) {
      loadTemplates(templatePage, templatePage > 1);
    } else {
      setTemplates([]);
      setTemplateTotal(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.messageType, debouncedSearchQuery, templatePage]);

  // Load selected templates data when templateIds change (for display)
  useEffect(() => {
    if (formData.templateIds.length > 0) {
      const selected = templates.filter(t => formData.templateIds.includes(t.id));
      // Also check if we need to fetch any templates that aren't in current page
      const missingIds = formData.templateIds.filter(id => !templates.some(t => t.id === id));
      if (missingIds.length > 0) {
        // Fetch missing templates individually if needed
        Promise.all(missingIds.map(id => communicationService.getTemplateById(id)))
          .then(fetched => {
            const allSelected = [...selected, ...fetched];
            setSelectedTemplatesData(allSelected);
            // Clear runtime message if no runtime templates are selected
            const hasRuntimeTemplate = allSelected.some(t => t.is_runtime_message);
            if (!hasRuntimeTemplate && formData.runtimeMessage) {
              setFormData(prev => ({ ...prev, runtimeMessage: '' }));
            }
          })
          .catch(err => console.error('Error fetching selected templates:', err));
      } else {
        setSelectedTemplatesData(selected);
        // Clear runtime message if no runtime templates are selected
        const hasRuntimeTemplate = selected.some(t => t.is_runtime_message);
        if (!hasRuntimeTemplate && formData.runtimeMessage) {
          setFormData(prev => ({ ...prev, runtimeMessage: '' }));
        }
      }
    } else {
      setSelectedTemplatesData([]);
      // Clear runtime message when no templates selected
      if (formData.runtimeMessage) {
        setFormData(prev => ({ ...prev, runtimeMessage: '' }));
      }
    }
  }, [formData.templateIds, templates]);

  const loadCities = async () => {
    try {
      const { data } = await supabase.from('city_registration_settings').select('city');
      setCities(data?.map(c => c.city) || []);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const handleChange = (field, value) => {
    if (field === 'messageType') {
      // Reset templateIds when messageType changes
      setFormData(prev => ({ 
        ...prev, 
        [field]: value,
        templateIds: [] // Clear selections when message type changes
      }));
      setTemplateSearchQuery(''); // Clear search when message type changes
      setTemplatePage(1); // Reset pagination
      setSelectedTemplatesData([]); // Clear selected templates data
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleCriteriaChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      recipientCriteria: {
        ...prev.recipientCriteria,
        [field]: value
      }
    }));
  };

  const handleTemplateToggle = async (templateId) => {
    // Find template in current templates or selected templates data
    let template = templates.find(t => t.id === templateId) || selectedTemplatesData.find(t => t.id === templateId);
    
    // If template not found in current page, fetch it
    if (!template) {
      try {
        template = await communicationService.getTemplateById(templateId);
      } catch (error) {
        console.error('Error fetching template:', error);
        return;
      }
    }

    setFormData(prev => {
      const isSelected = prev.templateIds.includes(template.id);
      let newTemplateIds = [];

      if (isSelected) {
        // Remove template
        newTemplateIds = prev.templateIds.filter(id => id !== template.id);
      } else {
        // Add template with validation
        if (prev.messageType === 'SMS') {
          // Only allow 1 SMS template
          if (template.message_type === 'SMS') {
            newTemplateIds = [template.id]; // Replace any existing selection
          }
        } else if (prev.messageType === 'WHATSAPP') {
          // Only allow 1 WhatsApp template
          if (template.message_type === 'WHATSAPP') {
            newTemplateIds = [template.id]; // Replace any existing selection
          }
        } else if (prev.messageType === 'BOTH') {
          // Allow 1 SMS + 1 WhatsApp (exactly 2)
          // Use selectedTemplatesData for accurate type checking
          const currentSms = selectedTemplatesData.filter(t => t.message_type === 'SMS').map(t => t.id);
          const currentWhatsapp = selectedTemplatesData.filter(t => t.message_type === 'WHATSAPP').map(t => t.id);

          if (template.message_type === 'SMS') {
            if (currentSms.length === 0) {
              newTemplateIds = [...prev.templateIds, template.id];
            } else {
              // Replace existing SMS template
              newTemplateIds = [...prev.templateIds.filter(id => !currentSms.includes(id)), template.id];
            }
          } else if (template.message_type === 'WHATSAPP') {
            if (currentWhatsapp.length === 0) {
              newTemplateIds = [...prev.templateIds, template.id];
            } else {
              // Replace existing WhatsApp template
              newTemplateIds = [...prev.templateIds.filter(id => !currentWhatsapp.includes(id)), template.id];
            }
          }
        } else {
          // No messageType selected, allow selection
          newTemplateIds = [...prev.templateIds, template.id];
        }
      }

      return { ...prev, templateIds: newTemplateIds };
    });
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Campaign name is required';
    }
    
    if (!formData.messageType) {
      newErrors.messageType = 'Please select message type (SMS/WhatsApp/Both)';
    }
    
    if (formData.templateIds.length === 0) {
      newErrors.templateIds = 'At least one template is required';
    } else {
      // Get selected templates (use selectedTemplatesData if available, otherwise filter from templates)
      const selectedTemplates = selectedTemplatesData.length > 0 
        ? selectedTemplatesData 
        : templates.filter(t => formData.templateIds.includes(t.id));
      
      // If we still don't have all templates, fetch them
      if (selectedTemplates.length < formData.templateIds.length) {
        // This shouldn't happen often, but handle it gracefully
        const missingIds = formData.templateIds.filter(id => !selectedTemplates.some(t => t.id === id));
        // For validation, we'll check runtime status from what we have
      }
      
      // Check if any selected template is runtime
      const hasRuntimeTemplate = selectedTemplates.some(t => t.is_runtime_message);
      
      // Validate runtime message if runtime templates are selected
      if (hasRuntimeTemplate && !formData.runtimeMessage?.trim()) {
        newErrors.runtimeMessage = 'Runtime message is required when using runtime templates';
      }
      
      // Validate template selection based on messageType
      const smsTemplates = selectedTemplates.filter(t => t.message_type === 'SMS');
      const whatsappTemplates = selectedTemplates.filter(t => t.message_type === 'WHATSAPP');

      if (formData.messageType === 'SMS') {
        if (smsTemplates.length !== 1) {
          newErrors.templateIds = 'Please select exactly 1 SMS template';
        }
      } else if (formData.messageType === 'WHATSAPP') {
        if (whatsappTemplates.length !== 1) {
          newErrors.templateIds = 'Please select exactly 1 WhatsApp template';
        }
      } else if (formData.messageType === 'BOTH') {
        if (smsTemplates.length !== 1 || whatsappTemplates.length !== 1) {
          newErrors.templateIds = 'Please select exactly 1 SMS template and 1 WhatsApp template';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setIsSaving(true);
    try {
      const campaignData = {
        name: formData.name,
        templateIds: formData.templateIds,
        campaignType: formData.campaignType,
        description: formData.description,
        recipientCriteria: formData.recipientCriteria,
        scheduledAt: formData.scheduledAt ? new Date(formData.scheduledAt).toISOString() : null,
        runtimeMessage: formData.runtimeMessage?.trim() || null
      };

      await onSave(campaignData, campaign?.id);
      onClose();
    } catch (error) {
      console.error('Error saving campaign:', error);
      setErrors({ submit: error.message || 'Failed to save campaign' });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const statusOptions = ['draft', 'submitted', 'approved', 'rejected'];
  const ageCategories = [
    { value: 'boys_u14', label: 'Boys U14' },
    { value: 'girls_u15', label: 'Girls U15' },
    { value: 'boys_u16', label: 'Boys U16' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {campaign ? 'Edit Campaign' : 'Create New Campaign'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="p-6 space-y-6">
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span className="text-sm text-red-700">{errors.submit}</span>
            </div>
          )}

          {/* Campaign Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Campaign Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="e.g., Registration Deadline Reminder"
            />
            {errors.name && (
              <p className="text-xs text-red-600 mt-1">{errors.name}</p>
            )}
          </div>

          {/* Message Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.messageType}
              onChange={(e) => handleChange('messageType', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.messageType ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Select message type...</option>
              <option value="SMS">SMS</option>
              <option value="WHATSAPP">WhatsApp</option>
              <option value="BOTH">Both (SMS + WhatsApp)</option>
            </select>
            {errors.messageType && (
              <p className="text-xs text-red-600 mt-1">{errors.messageType}</p>
            )}
            {formData.messageType && (
              <p className="text-xs text-gray-500 mt-1">
                {formData.messageType === 'SMS' && 'Select 1 SMS template'}
                {formData.messageType === 'WHATSAPP' && 'Select 1 WhatsApp template'}
                {formData.messageType === 'BOTH' && 'Select 1 SMS template and 1 WhatsApp template'}
              </p>
            )}
          </div>

          {/* Campaign Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Campaign Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.campaignType}
              onChange={(e) => handleChange('campaignType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="FIXED">Fixed (Same message to all)</option>
              <option value="DYNAMIC">Dynamic (Personalized per recipient)</option>
            </select>
          </div>

          {/* Templates Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Templates <span className="text-red-500">*</span>
            </label>
            {!formData.messageType ? (
              <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                <p className="text-sm text-gray-500">Please select a message type first</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Selected Templates Display */}
                {selectedTemplatesData.length > 0 && (
                  <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="text-xs font-semibold text-blue-900 mb-2">Selected Templates:</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedTemplatesData.map(template => (
                        <span
                          key={template.id}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-blue-300 rounded text-xs text-gray-700"
                        >
                          {template.name}
                          <span className="text-gray-500">({template.message_type})</span>
                          <button
                            onClick={() => handleTemplateToggle(template.id)}
                            className="ml-1 hover:text-red-600"
                            title="Remove"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Search Input */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={templateSearchQuery}
                    onChange={(e) => setTemplateSearchQuery(e.target.value)}
                    placeholder="Search templates by name or type..."
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  {templateSearchQuery && (
                    <button
                      onClick={() => setTemplateSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors"
                      title="Clear search"
                    >
                      <XCircle className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                </div>

                {/* Templates List */}
                <div className="border border-gray-300 rounded-lg p-4 max-h-64 overflow-y-auto">
                  {(() => {
                  // Filter templates based on messageType (server-side filtering already applied, but filter for BOTH)
                  let filteredTemplates = templates;
                  if (formData.messageType === 'BOTH') {
                    // For BOTH, we need to filter client-side since server returns all
                    filteredTemplates = templates.filter(t => t.message_type === 'SMS' || t.message_type === 'WHATSAPP');
                  }
                  // For SMS and WHATSAPP, server already filtered, so use templates as-is

                  // Get selected templates for validation (use selectedTemplatesData for accurate data)
                  const selectedTemplates = selectedTemplatesData.length > 0 
                    ? selectedTemplatesData 
                    : templates.filter(t => formData.templateIds.includes(t.id));
                  const smsSelected = selectedTemplates.filter(t => t.message_type === 'SMS');
                  const whatsappSelected = selectedTemplates.filter(t => t.message_type === 'WHATSAPP');

                  // Determine if checkbox should be disabled
                  const isDisabled = (template) => {
                    if (formData.messageType === 'SMS') {
                      return template.message_type !== 'SMS' || (smsSelected.length >= 1 && !formData.templateIds.includes(template.id));
                    } else if (formData.messageType === 'WHATSAPP') {
                      return template.message_type !== 'WHATSAPP' || (whatsappSelected.length >= 1 && !formData.templateIds.includes(template.id));
                    } else if (formData.messageType === 'BOTH') {
                      if (template.message_type === 'SMS') {
                        return smsSelected.length >= 1 && !formData.templateIds.includes(template.id);
                      } else if (template.message_type === 'WHATSAPP') {
                        return whatsappSelected.length >= 1 && !formData.templateIds.includes(template.id);
                      }
                    }
                    return false;
                  };

                  if (filteredTemplates.length === 0 && !templateLoading) {
                    return (
                      <p className="text-sm text-gray-500 text-center py-4">
                        {debouncedSearchQuery.trim() 
                          ? `No templates found matching "${debouncedSearchQuery}"`
                          : `No ${formData.messageType === 'BOTH' ? '' : formData.messageType} templates available`}
                      </p>
                    );
                  }

                  if (templateLoading && filteredTemplates.length === 0) {
                    return (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <span className="ml-2 text-sm text-gray-500">Loading templates...</span>
                      </div>
                    );
                  }

                  // Group templates by message_type for BOTH
                  if (formData.messageType === 'BOTH') {
                    const smsTemplates = filteredTemplates.filter(t => t.message_type === 'SMS');
                    const whatsappTemplates = filteredTemplates.filter(t => t.message_type === 'WHATSAPP');
                    
                    return (
                      <div className="space-y-4">
                        {/* SMS Templates Section */}
                        {smsTemplates.length > 0 && (
                          <div>
                            <h4 className="text-xs font-semibold text-gray-700 mb-2">
                              SMS Templates {smsSelected.length > 0 && `(Selected: ${smsSelected[0].name})`}
                              {debouncedSearchQuery.trim() && ` (${smsTemplates.length} of ${templateTotal} found)`}
                            </h4>
                            <div className="space-y-2">
                              {smsTemplates.map(template => (
                              <label
                                key={template.id}
                                className={`flex items-start gap-3 p-2 rounded cursor-pointer ${
                                  isDisabled(template) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={formData.templateIds.includes(template.id)}
                                  onChange={() => handleTemplateToggle(template.id)}
                                  disabled={isDisabled(template)}
                                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900">{template.name}</p>
                                  <p className="text-xs text-gray-500">{template.message_type} • {template.template_type}</p>
                                </div>
                              </label>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* WhatsApp Templates Section */}
                        {whatsappTemplates.length > 0 && (
                          <div>
                            <h4 className="text-xs font-semibold text-gray-700 mb-2">
                              WhatsApp Templates {whatsappSelected.length > 0 && `(Selected: ${whatsappSelected[0].name})`}
                              {debouncedSearchQuery.trim() && ` (${whatsappTemplates.length} of ${templateTotal} found)`}
                            </h4>
                            <div className="space-y-2">
                              {whatsappTemplates.map(template => (
                              <label
                                key={template.id}
                                className={`flex items-start gap-3 p-2 rounded cursor-pointer ${
                                  isDisabled(template) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={formData.templateIds.includes(template.id)}
                                  onChange={() => handleTemplateToggle(template.id)}
                                  disabled={isDisabled(template)}
                                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900">{template.name}</p>
                                  <p className="text-xs text-gray-500">{template.message_type} • {template.template_type}</p>
                                </div>
                              </label>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }

                  // Single message type (SMS or WhatsApp)
                  const hasMore = filteredTemplates.length < templateTotal;
                  
                  return (
                    <div className="space-y-2">
                      {debouncedSearchQuery.trim() && (
                        <p className="text-xs text-gray-500 mb-2">
                          Showing {filteredTemplates.length} of {templateTotal} template(s)
                        </p>
                      )}
                      {!debouncedSearchQuery.trim() && templateTotal > 50 && (
                        <p className="text-xs text-gray-500 mb-2">
                          Showing first {filteredTemplates.length} of {templateTotal} templates. Use search to find specific templates.
                        </p>
                      )}
                      {filteredTemplates.map(template => {
                        const disabled = isDisabled(template);
                        return (
                          <label
                            key={template.id}
                            className={`flex items-start gap-3 p-2 rounded cursor-pointer ${
                              disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={formData.templateIds.includes(template.id)}
                              onChange={() => handleTemplateToggle(template.id)}
                              disabled={disabled}
                              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                            />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">{template.name}</p>
                              <p className="text-xs text-gray-500">{template.message_type} • {template.template_type}</p>
                            </div>
                          </label>
                        );
                      })}
                      {/* Load More Button */}
                      {hasMore && !templateLoading && (
                        <button
                          onClick={() => setTemplatePage(prev => prev + 1)}
                          className="w-full mt-2 px-4 py-2 text-sm text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                        >
                          Load More ({templateTotal - filteredTemplates.length} remaining)
                        </button>
                      )}
                      {templateLoading && filteredTemplates.length > 0 && (
                        <div className="flex items-center justify-center py-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          <span className="ml-2 text-xs text-gray-500">Loading more...</span>
                        </div>
                      )}
                    </div>
                  );
                })()}
                </div>
              </div>
            )}
            {errors.templateIds && (
              <p className="text-xs text-red-600 mt-1">{errors.templateIds}</p>
            )}
            {formData.messageType && formData.templateIds.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Selected: {formData.templateIds.length} template(s)
              </p>
            )}
          </div>

          {/* Runtime Message Input */}
          {(() => {
            const hasRuntimeTemplate = selectedTemplatesData.some(t => t.is_runtime_message);
            if (!hasRuntimeTemplate) return null;
            
            return (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Runtime Message <span className="text-red-500">*</span>
                  <span className="text-xs font-normal text-gray-500 ml-2">
                    (Required for runtime templates - plain text, no variables)
                  </span>
                </label>
                <textarea
                  value={formData.runtimeMessage}
                  onChange={(e) => {
                    handleChange('runtimeMessage', e.target.value);
                  }}
                  placeholder="Enter the message content that will be sent for runtime templates..."
                  rows={4}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.runtimeMessage ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.runtimeMessage && (
                  <p className="text-xs text-red-600 mt-1">{errors.runtimeMessage}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  This message will be used for all runtime templates in this campaign.
                </p>
              </div>
            );
          })()}

          {/* Recipient Criteria */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recipient Criteria
            </label>
            <div className="border border-gray-300 rounded-lg p-4 space-y-4">
              {/* Status */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">Status</label>
                <div className="flex flex-wrap gap-2">
                  {statusOptions.map(status => (
                    <button
                      key={status}
                      onClick={() => {
                        const current = formData.recipientCriteria.status || [];
                        const newStatus = current.includes(status)
                          ? current.filter(s => s !== status)
                          : [...current, status];
                        handleCriteriaChange('status', newStatus);
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        (formData.recipientCriteria.status || []).includes(status)
                          ? 'bg-blue-600 text-white' :'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                      }`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>

              {/* City */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">City</label>
                <select
                  value={formData.recipientCriteria.city || ''}
                  onChange={(e) => handleCriteriaChange('city', e.target.value || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="">All Cities</option>
                  {cities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>

              {/* Age Category */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">Age Category</label>
                <div className="flex flex-wrap gap-2">
                  {ageCategories.map(cat => (
                    <button
                      key={cat.value}
                      onClick={() => {
                        const current = formData.recipientCriteria.ageCategory || [];
                        const newAgeCat = current.includes(cat.value)
                          ? current.filter(ac => ac !== cat.value)
                          : [...current, cat.value];
                        handleCriteriaChange('ageCategory', newAgeCat);
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        (formData.recipientCriteria.ageCategory || []).includes(cat.value)
                          ? 'bg-blue-600 text-white' :'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Recipient Type */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Recipient Type <span className="text-gray-500 font-normal">(Who should receive the message?)</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 'POC', label: 'POCs (Submitter)' },
                    { value: 'COACH', label: 'Coaches' },
                    { value: 'PLAYER', label: 'Players' }
                  ].map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => {
                        const current = formData.recipientCriteria.recipientType || ['POC'];
                        const newType = current.includes(type.value)
                          ? current.filter(t => t !== type.value)
                          : [...current, type.value];
                        // Ensure at least one type is selected
                        if (newType.length === 0) {
                          handleCriteriaChange('recipientType', ['POC']);
                        } else {
                          handleCriteriaChange('recipientType', newType);
                        }
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        (formData.recipientCriteria.recipientType || ['POC']).includes(type.value)
                          ? 'bg-blue-600 text-white' :'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Select one or more recipient types. POCs are the people who submitted the registration.
                </p>
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="Campaign description..."
            />
          </div>

          {/* Schedule */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Schedule (Optional)
            </label>
            <div className="flex items-center gap-3">
              <input
                type="datetime-local"
                value={formData.scheduledAt || ''}
                onChange={(e) => handleChange('scheduledAt', e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {formData.scheduledAt && (
                <button
                  onClick={() => handleChange('scheduledAt', null)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            disabled={isSaving}
            iconName="Save"
          >
            {isSaving ? 'Saving...' : 'Save Campaign'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CampaignModal;

